import React, { useEffect, useRef, useState } from "react";
import classes from "./SecondaryNav.module.scss";
import useIsMobile from "../../../hooks/useIsMobile";
import Chevron from "../Icons/Chevron";
import Modal from "../Modal/Modal";
import { COUNTRY_DETAIL_SECONDARY_NAV_ITEMS } from "../../../utils/constants";

const slugify = (value: string) => value.trim().toLowerCase().replace(/\s+/g, "-");

type itemType = {
  id: string;
  title: string;
  href: string;
  icon: string;
};

interface SecondaryNavProps {
  items: itemType[];
  onSelectTab?: (tab: string) => void;
  headerHeight: number | null;
}

const SecondaryNav = ({ items = COUNTRY_DETAIL_SECONDARY_NAV_ITEMS, onSelectTab, headerHeight }: SecondaryNavProps) => {
  const isMobile = useIsMobile(1000);
  const [activeTab, setActiveTab] = useState("why");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isClickScrolling = useRef(false);
  const clickScrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ensures the on-reload scroll restore only fires once.
  const didRestore = useRef(false);

  const scrollToSection = (tab: string, behavior: ScrollBehavior = "smooth") => {
    setActiveTab(tab);
    onSelectTab?.(tab);

    const sectionId = slugify(tab);
    const section = document.getElementById(sectionId);
    if (section) {
      const url = new URL(window.location.href);
      url.hash = sectionId;
      window.history.replaceState(null, "", url);

      const offset = (headerHeight ?? 0) + 54;
      const top = section.getBoundingClientRect().top + window.scrollY - offset;

      isClickScrolling.current = true;
      if (clickScrollTimeout.current) clearTimeout(clickScrollTimeout.current);
      clickScrollTimeout.current = setTimeout(() => {
        isClickScrolling.current = false;
      }, 800);

      window.scrollTo({ top, behavior });
    }
  };

  const handleTabClick = (tab: string) => () => scrollToSection(tab, "smooth");

  // Scroll spy: highlight the tab whose section is currently under the nav.
  useEffect(() => {
    if (items?.length === 0) return;

    const offset = (headerHeight ?? 0) + 54;

    const sections = items?.map((item) => document.getElementById(slugify(item?.id ?? ""))).filter((el): el is HTMLElement => el !== null);

    if (sections?.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;

        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length === 0) return;

        const matched = items.find((item) => slugify(item.id) === visible[0].target.id);
        if (matched) {
          setActiveTab(matched.id);
          onSelectTab?.(matched.id);
        }
      },
      {
        rootMargin: `-${offset}px 0px -55% 0px`,
        threshold: 0,
      },
    );

    sections?.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [items, headerHeight, onSelectTab]);

  useEffect(() => {
    return () => {
      if (clickScrollTimeout.current) clearTimeout(clickScrollTimeout.current);
    };
  }, []);

  const menuClickHandler = () => {
    setIsMenuOpen(true);
  };

  return (
    <>
      {!isMobile ? (
        <div style={{ top: `${headerHeight}px` }} className={`${classes.secondaryNavContainer} ${isMobile ? classes["hide-scrollbar"] : ""}`}>
          {items &&
            items.length > 0 &&
            items.map((navItem) => (
              <div
                key={navItem.id}
                className={`${classes.secondaryNavItem} ${activeTab === navItem.id ? classes.secondaryNavItemActive : ""}`}
                onClick={handleTabClick(navItem.id)}
              >
                {navItem.title}
                {activeTab === navItem.id && <div className={classes.activeIndicator}></div>}
              </div>
            ))}
        </div>
      ) : (
        <div style={{ top: `${headerHeight}px` }} className={classes["secondary-menu"]} onClick={menuClickHandler}>
          <div className={classes["secondary-menu-item"]}>
            <p>{items.find((item) => item.id === activeTab)?.title}</p>
            <Chevron width={8} height={14} color="#0E1B2C" />
          </div>

          <Modal
            isOpen={isMenuOpen}
            onClose={() => {
              setIsMenuOpen(false);
            }}
          >
            <div className={classes["modal-content"]}>
              {items &&
                items.length > 0 &&
                items.map((navItem) => (
                  <div
                    key={navItem.id}
                    className={`${classes["modal-content-item"]} ${activeTab === navItem.id ? classes.modalContentItemActive : ""}`}
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleTabClick(navItem.id)();
                    }}
                  >
                    <p>{navItem.title}</p>
                  </div>
                ))}
            </div>
          </Modal>
        </div>
      )}
    </>
  );
};

export default SecondaryNav;
